import axios from "axios";
import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { money } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const METRICS = [
  ["income", "Gelir"],
  ["gross", "Ciro"],
  ["expense", "Gider"],
  ["net", "Net"],
];
const compact = v => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v);
const TICK = { fontSize: 11 };
const LEGEND_STYLE = { fontSize: 12 };

export default function YearlyChart({ month, refreshKey }) {
  const thisYear = Number((month || new Date().toISOString().slice(0, 7)).slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [currency, setCurrency] = useState("TRY");
  const [metric, setMetric] = useState("income");
  const [payload, setPayload] = useState(null);

  useEffect(() => { setYear(thisYear); }, [thisYear]);

  useEffect(() => {
    axios.get(`${API}/reports/yearly`, { params: { year } })
      .then(r => setPayload(r.data)).catch(() => setPayload(null));
  }, [year, refreshKey]);

  if (!payload) return null;

  const suffix = `${metric}_${currency === "TRY" ? "try" : "usd"}`;
  const rows = payload.data.map(row => ({
    label: MONTHS[row.month - 1],
    [String(payload.year)]: row[`current_${suffix}`],
    [String(payload.previous_year)]: row[`previous_${suffix}`],
  }));
  const sum = key => payload.data.reduce((total, row) => total + row[`${key}_${suffix}`], 0);
  const current = sum("current");
  const previous = sum("previous");
  const delta = previous ? ((current - previous) / previous) * 100 : null;
  const hasData = current || previous;

  return (
    <section className="panel yearly-panel" data-testid="yearly-chart">
      <div className="panel-head">
        <div>
          <p className="eyebrow">YILLIK KARŞILAŞTIRMA</p>
          <h3>{payload.year} — {payload.previous_year} ay ay karşılaştırma</h3>
        </div>
        <div className="trend-controls yearly-controls">
          <div className="segmented mini" data-testid="yearly-year-switch">
            <button type="button" className={year === thisYear - 1 ? "selected" : ""} data-testid="yearly-year-prev"
              onClick={() => setYear(thisYear - 1)}>{thisYear - 1}</button>
            <button type="button" className={year === thisYear ? "selected" : ""} data-testid="yearly-year-current"
              onClick={() => setYear(thisYear)}>{thisYear}</button>
          </div>
          <div className="segmented mini" data-testid="yearly-metric-switch">
            {METRICS.map(([key, label]) => (
              <button key={key} type="button" className={metric === key ? "selected" : ""} data-testid={`yearly-metric-${key}`}
                onClick={() => setMetric(key)}>{label}</button>
            ))}
          </div>
          <div className="segmented mini" data-testid="yearly-currency-switch">
            <button type="button" className={currency === "TRY" ? "selected" : ""} data-testid="yearly-currency-try" onClick={() => setCurrency("TRY")}>TL</button>
            <button type="button" className={currency === "USD" ? "selected" : ""} data-testid="yearly-currency-usd" onClick={() => setCurrency("USD")}>USD</button>
          </div>
        </div>
      </div>

      <div className="yearly-totals" data-testid="yearly-totals">
        <span>{payload.year} <b>{money(current, currency)}</b></span>
        <span className="muted">{payload.previous_year} <b>{money(previous, currency)}</b></span>
        {delta != null && (
          <span className={delta >= 0 ? "delta up" : "delta down"} data-testid="yearly-delta">
            {delta >= 0 ? "▲" : "▼"} %{Math.abs(delta).toFixed(1)}
          </span>
        )}
      </div>

      {hasData ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={3}>
            <CartesianGrid stroke="#e7edf3" vertical={false}/>
            <XAxis dataKey="label" tick={TICK} axisLine={false} tickLine={false}/>
            <YAxis tickFormatter={compact} tick={TICK} axisLine={false} tickLine={false} width={54}/>
            <Tooltip formatter={value => money(value, currency)}/>
            <Legend wrapperStyle={LEGEND_STYLE}/>
            <Bar dataKey={String(payload.previous_year)} fill="#cbd5e1" radius={[4, 4, 0, 0]}/>
            <Bar dataKey={String(payload.year)} fill="#93257e" radius={[4, 4, 0, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="empty-table" data-testid="yearly-empty">Bu yıllar için veri yok.</div>
      )}
    </section>
  );
}
